import { AiFillInstagram } from "react-icons/ai";
import {
  FaBehance,
  FaDiscord,
  FaFacebook,
  FaTelegramPlane,
  FaTiktok,
  FaTumblr,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

import { ChannelInfo, ChannelKey } from "./types";

import { InlineError } from "@/modules/form-field/errors";

export const CHANNEL_KEY_TO_CHANNEL_INFO: Record<ChannelKey, ChannelInfo> = {
  facebook: {
    icon: <FaFacebook />,
    label: "Facebook",
    placeholder: "Facebook username, user ID, or URL",
    acceptedDomains: ["facebook.com", "fb.com", "fb.gg", "fb.me"],
    handleToUrl: (handle) => {
      if (/^[0-9]+$/.test(handle)) {
        InlineError.assert$PunishAfterEditing(
          /^[0-9]{4,15}$/.test(handle),
          "Invalid Facebook user ID"
        );
        return `https://www.facebook.com/profile.php?id=${handle}`;
      }

      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z.]{5,50}$/.test(handle),
        "Invalid Facebook username"
      );
      return `https://www.facebook.com/${handle}`;
    },
  },
  twitter: {
    icon: <FaXTwitter />,
    label: "Twitter",
    placeholder: "Twitter username or URL",
    acceptedDomains: ["twitter.com", "t.co", "twttr.com", "x.com"],
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_]{4,15}$/.test(handle),
        "Invalid Twitter username"
      );
      return `https://x.com/${handle}`;
    },
  },
  instagram: {
    icon: <AiFillInstagram />,
    label: "Instagram",
    placeholder: "Instagram username or URL",
    acceptedDomains: ["instagram.com", "ig.me"],
    // https://www.dummies.com/article/technology/social-media/instagram/choosing-instagram-name-username-business-249576
    // An Instagram username is limited to 30 characters and must contain
    // only letters, numbers, periods, and underscores. You can't include
    // symbols or other punctuation marks as a part of your username.
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_.]{5,30}$/.test(handle),
        "Invalid Instagram username"
      );
      return `https://www.instagram.com/${handle}/`;
    },
  },
  telegram: {
    icon: <FaTelegramPlane />,
    label: "Telegram",
    placeholder: "Telegram username or URL",
    acceptedDomains: ["t.me", "telegram.me", "telegram.org"],
    // https://telegram.org/faq#q-what-can-i-use-as-my-username
    // You can use a-z, 0-9 and underscores. Usernames are case-insensitive,
    // but Telegram will store your capitalization preferences (e.g. Telegram
    // and TeleGram is the same user).
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_]{5,32}$/.test(handle),
        "Invalid Telegram username"
      );
      return `https://t.me/${handle}`;
    },
  },
  youtube: {
    icon: <FaYoutube />,
    label: "YouTube",
    placeholder: "YouTube channel ID, channel handle, or URL",
    acceptedDomains: ["youtube.com", "youtu.be", "yt.be"],
    // There are two cases: channel ID and channel handle
    // https://support.google.com/youtube/answer/6180214?sjid=14443947455404845908-AP
    handleToUrl: (handle) => {
      if (handle.length === 24 && handle.startsWith("UC")) {
        // https://stackoverflow.com/questions/25413707/regex-for-youtube-channel-url
        // Start with UC; 21 characters of [0-9a-zA-Z_-] (same as [\w-]); end with one of [AQgw]
        InlineError.assert$PunishAfterEditing(
          /^UC[0-9a-zA-Z_-]{21}[AQgw]$/,
          "Invalid Youtube channel ID"
        );
        return `https://www.youtube.com/channel/${handle}`;
      }

      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_.-]{3,30}$/,
        "Invalid Youtube channel handle"
      );
      return `https://www.youtube.com/@${handle}`;
    },
  },
  tiktok: {
    icon: <FaTiktok />,
    label: "TikTok",
    placeholder: "TikTok username or URL",
    acceptedDomains: ["tiktok.com"],
    // https://www.adobe.com/express/learn/blog/tiktok-username-ideas
    // TikTok usernames are limited to 24 characters. Usernames can only
    // contain letters, numbers, periods, and underscores.
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_.]{3,24}$/.test(handle),
        "Invalid TikTok username"
      );
      return `https://www.tiktok.com/@${handle}`;
    },
  },
  behance: {
    icon: <FaBehance />,
    label: "Behance",
    placeholder: "Behance handle or URL",
    acceptedDomains: ["behance.net", "be.net"],
    // Must be between 3 and 20 characters.
    // Please only use letters, numbers, dash or underscore characters
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_-]{3,20}$/.test(handle),
        "Invalid Behance handle"
      );
      return `https://www.behance.net/${handle}`;
    },
  },
  discord: {
    icon: <FaDiscord />,
    label: "Discord",
    placeholder: "Discord invite URL",
    acceptedDomains: [
      "discordapp.com",
      "discordapp.net",
      "discord.com",
      "discord.gg",
    ],
    // Must be between 3 and 20 characters.
    // Please only use letters, numbers, dash or underscore characters
    handleToUrl: (handle) => {
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z_-]{3,20}$/.test(handle),
        "Invalid Discord invite URL"
      );
      return `https://discord.gg/${handle}`;
    },
  },
  tumblr: {
    icon: <FaTumblr />,
    label: "Tumblr",
    placeholder: "Tumblr blog name or URL",
    acceptedDomains: ["tumblr.com"],
    handleToUrl: (handle) => {
      // Blog names can be up to 32 characters long, containing only letters, numbers, and hyphens.
      // However, hyphens cannot be used in the first or last position of a blog name.
      InlineError.assert$PunishAfterEditing(
        /^[0-9A-Za-z][0-9A-Za-z-]{1,30}[0-9A-Za-z]$/.test(handle),
        "Invalid Tumblr blog"
      );
      return `https://${handle}.tumblr.com/`;
    },
  },
};
